import { Container, DisplacementFilter, Sprite, TexturePool } from 'pixi.js'
import { hexCenter, subCenter, dividerY } from '../../utils/hexGeometry'
import { hexDistance } from '../../rules/hexGrid'
import { assertBeat } from './Beat'
import { PRESETS, DEFAULT_PRESET } from './reveal.config'
import { revealPlan } from './revealPlan'
import { effectBudget } from './effectBudget'
import { buildEffectTextures } from './effectTextures'
import { BloomFilter } from './bloomFilter'
import { ease } from './easing'
import { LOAD } from '../load.config'

// The reveal beat (Gate 1 items C and H): one clash -> the surplus missile's
// lob -> impact -> blast -> settle, on the war scene, from a fixture clash
// (rules output: corridor, aim cell, crater cells). Everything it does, and
// when, comes from one reveal.config.js preset (`hog` by default):
//   clash        attacker and defender shots meet, 80-spark burst
//   missile      launch -> crest -> impact, with its 60-particle trail
//   anticipation the missile stretches lengthwise just before impact
//   hit-stop     the beat freezes at impact
//   flash        white flash at the aim cell
//   blast        explosion flipbook (with bloom), spark burst, debris thrown,
//                heat haze, crater rings start
//   settle       smoke, debris landing, glow and haze fading
//   shake        the whole scene, from the end of the hit-stop
// Particle budget: the 400 impact sprites of item F are split between the
// flipbook, smoke, debris and sparks (effectBudget.js), so the peak stays 400.
// Implements the Beat interface (beats/Beat.js): the run controller, logger
// and sustained loop drive it. step() allocates nothing.
//
// Each play starts from the pre-clash look (this clash's crater cells,
// stamps and the structures it kills shown as before) and ends on the rules
// output's post-round state. Attaching the beat puts the scene in the
// pre-clash look; reset() and destroy() restore the rules output's state
// exactly, and criterion #8's check removes the beat first.
//
// Async: the effect textures are built (and uploaded) for this preset and
// this phone's resolution before the beat touches the scene.

// The clash to play: meta.revealCol, else the first clash with a crater.
export function revealClashOf(fixture) {
    const want = fixture.meta.revealCol
    return fixture.clashes.find(c => c.crater && (want == null || c.col === want)) ?? null
}

export async function createRevealBeat(stage, { preset = DEFAULT_PRESET } = {}) {
    const scene = stage.scene
    if (!scene) throw new Error('reveal beat needs a war scene')
    const cfg = PRESETS[preset]
    if (!cfg) throw new Error(`unknown reveal preset "${preset}"`)
    const clash = revealClashOf(scene.fixture)
    if (!clash) throw new Error(`fixture ${scene.name} has no crater clash`)
    const budget = effectBudget(cfg)
    const { R, root, textures } = scene
    const renderer = stage.app.renderer
    const fx = await buildEffectTextures(cfg, { R, resolution: renderer.resolution, renderer })
    if (stage.scene !== scene || root.destroyed) { fx.destroy(); return null } // the scene changed while loading

    const defSide = clash.crater.side
    const attSide = defSide === 'opp' ? 'self' : 'opp'
    const col = clash.col
    const aimS = clash.crater.aimS

    // ── Geometry (board coordinates, as the scene) ─────────────────────────
    const corridorX = hexCenter('self', col * 3 + 1, 0, R).x
    const attOrigin = { x: corridorX, y: hexCenter(attSide, col * 3 + 1, 2, R).y }
    const defOrigin = { x: corridorX, y: hexCenter(defSide, col * 3 + 1, 2, R).y }
    const bandY = dividerY(R)
    const meet = { x: corridorX, y: bandY + (defSide === 'opp' ? -1 : 1) * cfg.clash.meetOffsetR * R }
    const target = subCenter(defSide, aimS, R)

    // ── The crater, ring by ring (rules output: this clash's fresh cells) ──
    const cells = clash.crater.stageCells.flat()
    const maxRing = cells.reduce((m, s) => Math.max(m, hexDistance(s, aimS)), 0)
    const plan = revealPlan(cfg, { maxRing })
    const byKey = new Map()
    const walk = n => { if (n.gate1) byKey.set(n.gate1.key, n); for (const ch of n.children ?? []) walk(ch) }
    walk(root)
    const killed = new Set(clash.event.destroyedStructures ?? [])
    const cellFx = cells.map(s => {
        const crater = byKey.get(`crater:${defSide}:${s}`) ?? null
        const stamp = byKey.get(`stamp:${defSide}:${s}`) ?? null
        const structures = [...byKey.entries()]
            .filter(([k]) => k.startsWith('structure:') && k.endsWith(`:${defSide}:${s}`) && killed.has(k.split(':')[1]))
            .map(([, sp]) => sp)
        return {
            s, ring: hexDistance(s, aimS), crater, stamp, structures,
            baseScale: crater ? { x: crater.scale.x, y: crater.scale.y } : null,
            finalTex: structures.map(sp => sp.texture),
        }
    })
    const aliveTex = textures.get('structure')

    // ── Effect sprites, made once ──────────────────────────────────────────
    const layer = new Container()
    layer.label = 'beat:reveal'
    root.addChild(layer)
    const missileTex = textures.get('missile')
    const particleTex = textures.get('particle')
    const sized = (tex, w, h = w) => {
        const sp = new Sprite(tex)
        sp.anchor.set(0.5)
        sp.width = w
        sp.height = h
        sp.visible = false
        return sp
    }
    const mW = missileTex.gate1Size.w, mH = missileTex.gate1Size.h
    const attShot = sized(missileTex, mW * 0.8, mH * 0.8)
    const defShot = sized(missileTex, mW * 0.8, mH * 0.8)
    const missile = sized(missileTex, mW, mH)
    const pBase = particleTex.gate1Size.w
    const additive = sp => { sp.blendMode = 'add'; return sp }
    const flash = additive(sized(particleTex, cfg.flash.sizeR * R))
    const trail = Array.from({ length: LOAD.missileTrailParticles }, () => additive(sized(particleTex, pBase)))
    const N = budget.sparks
    const parts = Array.from({ length: N }, () => additive(sized(particleTex, pBase)))

    const explosionSize = cfg.explosion.sizeR * R
    const explosion = cfg.explosion.enabled ? sized(fx.explosion[0], explosionSize) : null
    if (explosion && cfg.explosion.blend === 'add') explosion.blendMode = 'add'
    const smoke = Array.from({ length: budget.smoke }, () => sized(fx.smoke[0], cfg.smoke.sizeR * R))
    const debris = Array.from({ length: budget.debris }, (_, j) => sized(fx.debris[j % fx.debris.length], cfg.debris.sizeR[1] * R))

    const debrisLayer = new Container()
    const smokeLayer = new Container()
    const glowLayer = new Container()
    debrisLayer.addChild(...debris)
    smokeLayer.addChild(...smoke)
    if (explosion) glowLayer.addChild(explosion)
    glowLayer.addChild(flash)
    layer.addChild(debrisLayer, smokeLayer, glowLayer, ...trail, ...parts, attShot, defShot, missile)

    // Post effects. Bloom on the explosion + flash; heat haze on the whole scene.
    const bloom = cfg.bloom.enabled
        ? new BloomFilter({ threshold: cfg.bloom.threshold, strength: cfg.bloom.strength, blur: cfg.bloom.blurR * R, quality: cfg.bloom.quality })
        : null
    if (bloom) { bloom.enabled = false; glowLayer.filters = [bloom] }
    let hazeSprite = null, haze = null
    if (cfg.distortion.enabled) {
        hazeSprite = new Sprite(fx.haze)
        hazeSprite.anchor.set(0.5)
        hazeSprite.width = hazeSprite.height = 2 * cfg.distortion.radiusR * R
        hazeSprite.position.set(target.x, target.y)
        layer.addChild(hazeSprite)
        haze = new DisplacementFilter({ sprite: hazeSprite, scale: 0, resolution: 'inherit' })
        haze.enabled = false
        root.filters = [haze]
    }

    // Particle state (typed arrays: no per-frame allocation).
    const px = new Float32Array(N), py = new Float32Array(N), vx = new Float32Array(N), vy = new Float32Array(N)
    const born = new Float32Array(N), life = new Float32Array(N), grow = new Float32Array(N)
    const alive = new Uint8Array(N)
    const S = smoke.length
    const sx0 = new Float32Array(S), sy0 = new Float32Array(S), svx = new Float32Array(S), svy = new Float32Array(S), sBorn = new Float32Array(S), sRot = new Float32Array(S)
    const D = debris.length
    const dx0 = new Float32Array(D), dy0 = new Float32Array(D), dvx = new Float32Array(D), dvy = new Float32Array(D), dvz = new Float32Array(D)
    const dAir = new Float32Array(D), dSize = new Float32Array(D), dSpin = new Float32Array(D)
    let emitted = 0, burstEmitted = 0, peakAlive = 0, lastT = 0
    const peakMix = { sparks: 0, explosion: 0, smoke: 0, debris: 0 }
    // Peak over the whole run (all plays of a sustained loop), read and
    // cleared once per run by stats().
    let runPeak = 0
    let rng = 1
    const rand = () => ((rng = (rng * 1103515245 + 12345) & 0x7fffffff) / 0x7fffffff)

    const riseEase = ease(cfg.clash.riseEase)
    const climbEase = ease(cfg.missile.riseEase)
    const dropEase = ease(cfg.missile.fallEase)
    const cellEase = ease(cfg.crater.cellEase)
    const shakeEase = ease(cfg.shake.ease)
    const hazeEase = ease(cfg.distortion.ease)
    const stretchEase = ease(cfg.anticipation.ease)
    const rootBase = { x: root.position.x, y: root.position.y }
    const pos = { x: 0, y: 0, h: 0 }
    const flightMs = cfg.missile.riseMs + cfg.missile.fallMs
    // The missile stays on screen through the hit-stop (frozen at the aim cell).
    const missileUntil = cfg.hitStopMs > 0 ? plan.hitStopEnd : plan.impactAt
    const exFrames = fx.explosion.length
    const smFrames = fx.smoke.length

    // Surplus missile at time t (ms from start) -> pos: ground path linear
    // with a sideways bow; height climbs to the crest, then drops.
    function missileAt(t) {
        const tf = Math.min(t, plan.impactAt) - plan.missileStart
        const p = Math.max(0, Math.min(1, tf / flightMs))
        const h = tf < cfg.missile.riseMs ? climbEase(tf / cfg.missile.riseMs) : 1 - dropEase((tf - cfg.missile.riseMs) / cfg.missile.fallMs)
        const dx = target.x - attOrigin.x, dy = target.y - attOrigin.y
        const len = Math.hypot(dx, dy) || 1
        const bow = Math.sin(Math.PI * p) * cfg.missile.bowR * R
        pos.x = attOrigin.x + dx * p + (-dy / len) * bow
        pos.y = attOrigin.y + dy * p + (dx / len) * bow - h * cfg.missile.liftR * R
        pos.h = h
    }

    function emit(i, x, y, speedMin, speedMax, lifeMin, lifeMax, t) {
        const ang = rand() * Math.PI * 2
        const sp = (speedMin + rand() * (speedMax - speedMin)) * R
        px[i] = x; py[i] = y; vx[i] = Math.cos(ang) * sp; vy[i] = Math.sin(ang) * sp
        born[i] = t; life[i] = lifeMin + rand() * (lifeMax - lifeMin)
        grow[i] = (cfg.sparks.sizeR[0] + rand() * (cfg.sparks.sizeR[1] - cfg.sparks.sizeR[0])) * R / pBase
        alive[i] = 1
    }

    // Smoke and debris start states, drawn from the seeded generator each play.
    function seedSettle() {
        const sm = cfg.smoke, db = cfg.debris
        for (let i = 0; i < S; i++) {
            const a = rand() * Math.PI * 2, r = Math.sqrt(rand()) * sm.spreadR * R
            const v = (sm.driftR[0] + rand() * (sm.driftR[1] - sm.driftR[0])) * R
            sx0[i] = target.x + Math.cos(a) * r; sy0[i] = target.y + Math.sin(a) * r
            svx[i] = Math.cos(a) * v; svy[i] = Math.sin(a) * v - sm.riseR * R
            sBorn[i] = plan.smokeStart + rand() * sm.spawnMs
            sRot[i] = rand() * Math.PI * 2
        }
        for (let j = 0; j < D; j++) {
            const a = rand() * Math.PI * 2
            const v = (db.speedR[0] + rand() * (db.speedR[1] - db.speedR[0])) * R
            dx0[j] = target.x; dy0[j] = target.y
            dvx[j] = Math.cos(a) * v; dvy[j] = Math.sin(a) * v
            dvz[j] = (db.upR[0] + rand() * (db.upR[1] - db.upR[0])) * R
            dAir[j] = (2 * dvz[j]) / (db.gravityR * R)                    // seconds airborne
            dSize[j] = (db.sizeR[0] + rand() * (db.sizeR[1] - db.sizeR[0])) * R
            dSpin[j] = (rand() * 2 - 1) * db.spinPerS
        }
    }

    function preClashLook() {
        for (const c of cellFx) {
            if (c.crater) c.crater.visible = false
            if (c.stamp) c.stamp.visible = false
            for (const sp of c.structures) sp.texture = aliveTex
        }
    }
    function finalLook() {
        for (const c of cellFx) {
            if (c.crater) { c.crater.visible = true; c.crater.scale.set(c.baseScale.x, c.baseScale.y); c.crater.alpha = 1 }
            if (c.stamp) c.stamp.visible = true
            c.structures.forEach((sp, i) => { sp.texture = c.finalTex[i] })
        }
        attShot.visible = defShot.visible = missile.visible = flash.visible = false
        if (explosion) explosion.visible = false
        for (let i = 0; i < trail.length; i++) trail[i].visible = false
        for (let i = 0; i < N; i++) { parts[i].visible = false; alive[i] = 0 }
        for (let i = 0; i < S; i++) smoke[i].visible = false
        for (let j = 0; j < D; j++) debris[j].visible = false
        if (bloom) bloom.enabled = false
        if (haze) haze.enabled = false
        root.position.set(rootBase.x, rootBase.y)
    }

    // GPU memory this beat adds: its textures, plus the post effects' render
    // targets (pool textures, sized as PixiJS sizes them; measured when the
    // prewarm has the explosion in place).
    const memory = { textureBytes: fx.stats.textureBytes, renderTargets: {}, renderTargetBytes: 0, totalBytes: fx.stats.textureBytes }
    function measureTargets() {
        const res = renderer.resolution
        const rt = (name, w, h, count) => {
            const o = TexturePool.getOptimalSize(w, h, res)
            memory.renderTargets[name] = `${count} x ${o.width}x${o.height}`
            memory.renderTargetBytes += count * o.width * o.height * 4
        }
        // Bloom: the filtered layer's input, bright pass, glow, and the blur's
        // passes: 5 targets of one size (counted with a WebGL allocation hook).
        if (bloom) { const b = glowLayer.getBounds(); rt('bloom', b.width + 2 * bloom.padding, b.height + 2 * bloom.padding, 5) }
        // Haze: the whole scene's input (clipped to the screen).
        if (haze) rt('haze', renderer.screen.width, renderer.screen.height, 1)
        memory.totalBytes = memory.textureBytes + memory.renderTargetBytes
    }

    let t0 = 0
    const beat = {
        name: `reveal-v2:${preset}`,
        preset,
        durationMs: plan.end,
        plan,
        start(now) {
            finalLook()
            preClashLook()
            t0 = now
            emitted = 0; burstEmitted = 0; peakAlive = 0; lastT = 0
            peakMix.sparks = peakMix.explosion = peakMix.smoke = peakMix.debris = 0
            rng = 1
            seedSettle()
            this.step(now)
        },
        step(now) {
            const t = now - t0
            const dt = Math.max(0, (t - lastT) / 1000)
            lastT = t

            // 1. Clash: attacker shot rises, defender darts out, they meet.
            if (cfg.clash.enabled && t >= plan.attackStart && t < plan.meetAt) {
                const p = riseEase((t - plan.attackStart) / cfg.clash.riseMs)
                attShot.visible = true
                attShot.position.set(attOrigin.x, attOrigin.y + (meet.y - attOrigin.y) * p)
                attShot.rotation = defSide === 'opp' ? 0 : Math.PI
                if (t >= plan.defenderStart) {
                    const q = (t - plan.defenderStart) / (plan.meetAt - plan.defenderStart)
                    defShot.visible = true
                    defShot.position.set(defOrigin.x, defOrigin.y + (meet.y - defOrigin.y) * q)
                    defShot.rotation = defSide === 'opp' ? Math.PI : 0
                }
            } else if (attShot.visible) {
                attShot.visible = false
                defShot.visible = false
            }
            if (cfg.clash.enabled && t >= plan.meetAt && burstEmitted < cfg.clash.burstParticles) {
                for (; burstEmitted < cfg.clash.burstParticles && burstEmitted < N; burstEmitted++) {
                    emit(burstEmitted, meet.x, meet.y, cfg.clash.burstSpeedR * 0.3, cfg.clash.burstSpeedR, cfg.clash.burstLifeMs * 0.6, cfg.clash.burstLifeMs, t)
                }
            }

            // 2. The missile's lob with its trail (frozen through the hit-stop).
            const flying = t >= plan.missileStart && t < missileUntil
            missile.visible = flying
            if (flying) {
                missileAt(t)
                const x = pos.x, y = pos.y, k = 1 + (cfg.missile.crestScale - 1) * pos.h
                missileAt(Math.min(t, plan.impactAt) - 16)
                missile.rotation = Math.atan2(y - pos.y, x - pos.x) + Math.PI / 2
                missile.position.set(x, y)
                // 3. Anticipation: the missile stretches along its flight just
                // before impact (held through the hit-stop). Height is lengthwise.
                let sw = 1, sl = 1
                if (plan.anticipation && t >= plan.anticipation.stretchStart) {
                    const e = stretchEase(Math.min(1, (t - plan.anticipation.stretchStart) / cfg.anticipation.stretchMs))
                    sw = 1 + (cfg.anticipation.width - 1) * e
                    sl = 1 + (cfg.anticipation.length - 1) * e
                }
                missile.width = mW * k * sw
                missile.height = mH * k * sl
            }
            for (let i = 0; i < trail.length; i++) {
                const tt = Math.min(t, plan.impactAt) - (i + 1) * cfg.missile.trailSpacingMs
                const on = flying && tt >= plan.missileStart
                const tp = trail[i]
                tp.visible = on
                if (on) {
                    missileAt(tt)
                    tp.position.set(pos.x, pos.y)
                    const k = 1 - i / trail.length
                    tp.alpha = 0.85 * k
                    tp.width = tp.height = pBase * (0.4 + 0.8 * k) * (1 + (cfg.missile.crestScale - 1) * pos.h)
                }
            }


            // 4. Flash at impact (also through the hit-stop).
            const flashOn = cfg.flash.enabled && t >= plan.impactAt && t < plan.flashEnd
            flash.visible = flashOn
            if (flashOn) { flash.position.set(target.x, target.y); flash.alpha = 1 - (t - plan.impactAt) / cfg.flash.ms }

            // 5. Blast: explosion flipbook, sparks (all N within emitMs). While
            // the glow is on, the flipbook sprite stays in place (alpha 0
            // outside its clip) so the bloom's bounds, and so its render
            // targets, keep one size: the size the prewarm allocated.
            let exOn = false
            if (explosion) {
                exOn = t >= plan.blastStart && t < plan.explosionEnd
                const placed = exOn || (bloom !== null && t >= plan.impactAt && t < plan.glowEnd)
                explosion.visible = placed
                if (placed) {
                    const f = exOn ? Math.min(exFrames - 1, Math.floor(((t - plan.blastStart) * cfg.explosion.fps) / 1000)) : 0
                    explosion.texture = fx.explosion[f]
                    explosion.position.set(target.x, target.y)
                    explosion.width = explosion.height = explosionSize
                    explosion.alpha = exOn ? 1 : 0
                }
            }
            if (t >= plan.blastStart) {
                const want = Math.min(N, Math.ceil(N * Math.min(1, (t - plan.blastStart) / cfg.sparks.emitMs)))
                for (; emitted < want; emitted++) {
                    emit(emitted, target.x, target.y, cfg.sparks.speedR[0], cfg.sparks.speedR[1], cfg.sparks.lifeMs[0], cfg.sparks.lifeMs[1], t)
                }
            }

            // 6. Shake: the whole scene, from the end of the hit-stop.
            if (cfg.shake.enabled && t >= plan.hitStopEnd && t < plan.shakeEnd) {
                const s = (t - plan.hitStopEnd) / cfg.shake.durationMs
                const a = cfg.shake.amplitudeR * R * (1 - shakeEase(s))
                const w = ((t - plan.hitStopEnd) / 1000) * cfg.shake.frequencyHz * Math.PI * 2
                root.position.set(rootBase.x + a * Math.sin(w), rootBase.y + a * Math.cos(w * 1.31))
            } else if (root.position.x !== rootBase.x || root.position.y !== rootBase.y) root.position.set(rootBase.x, rootBase.y)

            // 7. Bloom: full through the flash and the blast, fading after it.
            if (bloom) {
                const on = t >= plan.impactAt && t < plan.glowEnd
                bloom.enabled = on
                if (on) bloom.strength = cfg.bloom.strength * (t < plan.blastEnd ? 1 : 1 - (t - plan.blastEnd) / cfg.bloom.decayMs)
            }

            // 8. Heat haze around the impact, from the blast, fading out.
            if (haze) {
                const on = t >= plan.blastStart && t < plan.hazeEnd
                haze.enabled = on
                if (on) {
                    const p = (t - plan.blastStart) / cfg.distortion.durationMs
                    haze.scale.x = haze.scale.y = 2 * cfg.distortion.strengthR * R * (1 - hazeEase(p))
                    hazeSprite.rotation = ((t - plan.blastStart) / 1000) * cfg.distortion.spinPerS
                }
            }

            // Sparks: integrate, fade, count.
            let n = 0
            const drag = Math.max(0, 1 - cfg.sparks.drag * dt)
            for (let i = 0; i < N; i++) {
                const sp = parts[i]
                if (!alive[i]) { if (sp.visible) sp.visible = false; continue }
                const age = (t - born[i]) / life[i]
                if (age >= 1) { alive[i] = 0; sp.visible = false; continue }
                vx[i] *= drag; vy[i] *= drag
                px[i] += vx[i] * dt; py[i] += vy[i] * dt
                sp.visible = true
                sp.position.set(px[i], py[i])
                sp.alpha = 1 - age
                sp.width = sp.height = pBase * grow[i] * (1 - 0.6 * age)
                n++
            }
            const nSparks = n

            // 9. Smoke through the settle: born just before the blast ends, drifting, growing, fading.
            let nSmoke = 0
            for (let i = 0; i < S; i++) {
                const sp = smoke[i]
                const on = t >= sBorn[i] && t < plan.settleEnd
                sp.visible = on
                if (!on) continue
                const age = (t - sBorn[i]) / 1000
                const lifeP = (t - sBorn[i]) / (plan.settleEnd - sBorn[i])
                sp.texture = fx.smoke[Math.min(smFrames - 1, Math.floor(lifeP * smFrames))]
                sp.position.set(sx0[i] + svx[i] * age, sy0[i] + svy[i] * age)
                sp.width = sp.height = cfg.smoke.sizeR * R * (1 + (cfg.smoke.growTo - 1) * lifeP)
                sp.rotation = sRot[i]
                const fin = Math.min(1, (t - sBorn[i]) / cfg.smoke.fadeInMs)
                const fout = Math.min(1, (plan.settleEnd - t) / cfg.smoke.fadeOutMs)
                sp.alpha = cfg.smoke.alpha * fin * fout
                nSmoke++
            }

            // 10. Debris: thrown at the blast's start, airborne (lift + scale), landing, sliding to rest, fading.
            let nDebris = 0
            for (let j = 0; j < D; j++) {
                const sp = debris[j]
                const on = t >= plan.blastStart && t < plan.settleEnd
                sp.visible = on
                if (!on) continue
                const age = (t - plan.blastStart) / 1000
                const air = Math.min(age, dAir[j])
                const slide = age > dAir[j] ? (1 - Math.exp(-cfg.debris.friction * (age - dAir[j]))) / cfg.debris.friction : 0
                const z = age < dAir[j] ? dvz[j] * age - 0.5 * cfg.debris.gravityR * R * age * age : 0
                sp.position.set(dx0[j] + dvx[j] * (air + slide), dy0[j] + dvy[j] * (air + slide) - z)
                sp.width = sp.height = dSize[j] * (1 + (0.25 * z) / R)
                sp.rotation = dSpin[j] * air
                sp.alpha = Math.min(1, (plan.settleEnd - t) / cfg.debris.fadeOutMs)
                nDebris++
            }

            const nExplosion = exOn ? 1 : 0
            n = nSparks + nSmoke + nDebris + nExplosion
            if (n > peakAlive) { peakAlive = n; peakMix.sparks = nSparks; peakMix.smoke = nSmoke; peakMix.debris = nDebris; peakMix.explosion = nExplosion }
            if (n > runPeak) runPeak = n

            // 11. Crater, ring by ring, from the blast's start.
            for (let i = 0; i < cellFx.length; i++) {
                const c = cellFx[i]
                const startAt = plan.blastStart + c.ring * cfg.crater.ringDelayMs
                if (t < startAt) continue
                const p = (t - startAt) / cfg.crater.cellGrowMs
                if (c.crater) {
                    c.crater.visible = true
                    const sc = cfg.crater.cellStartScale + (1 - cfg.crater.cellStartScale) * cellEase(p)
                    c.crater.scale.set(c.baseScale.x * sc, c.baseScale.y * sc)
                    c.crater.alpha = Math.min(1, p * 2)
                }
                if (c.stamp && p >= 1) c.stamp.visible = true
                for (let j = 0; j < c.structures.length; j++) c.structures[j].texture = c.finalTex[j]
            }

            if (t >= plan.end) { finalLook(); return true }
            return false
        },
        reset() { finalLook() },
        destroy() {
            finalLook()
            if (haze && root.filters?.includes(haze)) root.filters = null
            layer.destroy({ children: true })
            fx.destroy()
        },
        stats() {
            const peak = runPeak
            runPeak = 0
            return {
                preset, clash: { fixture: scene.name, corridor: col + 1, aimS, side: defSide }, craterCells: cells.length, rings: maxRing + 1,
                peakParticles: peak, lastPlayPeakParticles: peakAlive, lastPlayPeakMix: { ...peakMix }, budget,
                trailParticles: trail.length, effectTextures: fx.stats, memory, plan,
            }
        },
    }
    finalLook()
    preClashLook()
    prewarm()
    return assertBeat(beat)

    // Draw every effect sprite (missile, trail, particles, flipbook frames,
    // smoke, debris; additive blending included) and run both post effects
    // for a few frames when the beat is attached, so shader compiles, texture
    // uploads and filter render targets happen before any run (a first-play
    // freeze of ~200 ms was measured on the iPhone without it). Sprites sit
    // off-screen; the filters need on-screen pixels, so the explosion sits on
    // the aim cell at near-zero alpha with the haze at zero strength. The
    // explosion and flash sit exactly where a play puts them, so the bloom's
    // render targets are allocated at the size a play uses. A play goes
    // through three post-effect states (bloom alone from the impact, bloom +
    // haze from the blast, haze alone after the glow), and the GPU compiles
    // separate state for each (drawing to the screen vs into the haze's
    // target: a first-play freeze of ~300 ms on the iPhone at the impact), so
    // the prewarm draws three frames of each.
    function prewarm(frames = 9) {
        const all = [attShot, defShot, missile, ...trail, ...parts, ...smoke, ...debris]
        for (const sp of all) { sp.visible = true; sp.alpha = 1; sp.position.set(-4000, -4000) }
        for (const sp of [explosion, flash]) if (sp) { sp.visible = true; sp.alpha = 0.004; sp.position.set(target.x, target.y) }
        measureTargets()
        if (haze) haze.scale.x = haze.scale.y = 0
        const phase = k => {
            const p = Math.min(2, Math.floor((k * 3) / frames))   // 0: bloom, 1: bloom + haze, 2: haze
            if (bloom) bloom.enabled = p < 2
            if (haze) haze.enabled = p > 0
        }
        phase(0)
        let n = frames
        const tick = () => {
            phase(frames - n + 1)
            // Cycle the flipbook and smoke frames so every frame is drawn once.
            if (explosion) explosion.texture = fx.explosion[(frames - n) % exFrames]
            for (let i = 0; i < S; i++) smoke[i].texture = fx.smoke[(i + frames - n) % smFrames]
            if (--n > 0) { requestAnimationFrame(tick); return }
            // Hide again unless a run has already taken over.
            if (t0 === 0) {
                for (const sp of all) sp.visible = false
                for (const sp of [explosion, flash]) if (sp) { sp.visible = false; sp.alpha = 1 }
                if (bloom) bloom.enabled = false
                if (haze) haze.enabled = false
            }
        }
        requestAnimationFrame(tick)
    }
}
